// Libs
import { useState } from "react";
import { useForm } from "react-hook-form";
import PaperAirplaneIcon from "@heroicons/react/24/outline/PaperAirplaneIcon";
import XMarkIcon from "@heroicons/react/24/outline/XMarkIcon";

// Local
import BaseInputText from "../BaseInputText";
import BaseButtonPrimary from "../BaseButtonPrimary";
import BaseButtonSecondary from "../BaseButtonSecondary";
import { DbRead } from "../../database";

interface FormData {
  emailAddress: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  templateName: string;
  siteKey: string;
}

export default function SendTestEmailDialog({
  isOpen,
  onClose,
  templateId,
  templateName,
  siteKey,
}: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setError(null);

    try {
      await DbRead.emailTemplates.sendTestEmail({
        templateId,
        siteKey,
        emailAddress: data.emailAddress,
      });

      setSuccess(true);
    } catch (err) {
      console.error("Error sending test email:", err);
      setError(
        err instanceof Error ? err.message : "Failed to send test email",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    setSuccess(false);
    setIsLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Send Test Email
          </h2>
          <button
            onClick={handleClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            disabled={isLoading}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {success ? (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <PaperAirplaneIcon className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              Test Email Sent!
            </h3>
            <p className="text-sm text-gray-600">
              The test email has been sent successfully.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <p className="mb-4 text-sm text-gray-600">
                Send a test email using the template "{templateName}" to verify
                how it looks and functions.
              </p>

              <label className="mb-2 block text-sm font-medium text-gray-700">
                Email Address
              </label>

              <BaseInputText
                {...register("emailAddress", {
                  required: "Email address is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Please enter a valid email address",
                  },
                })}
                admin={true}
                placeholder="Enter email address to send test to..."
                disabled={isLoading || success}
              />

              {errors.emailAddress && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.emailAddress.message}
                </p>
              )}
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <BaseButtonSecondary
                type="button"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </BaseButtonSecondary>
              <BaseButtonPrimary
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="h-4 w-4" />
                    Send Test Email
                  </>
                )}
              </BaseButtonPrimary>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
